/* Great-circle geodesy helpers — Haversine + bearing + point-to-segment. */

const R = 6371000; // Earth radius in meters
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

export function distance(a, b) {
  if (!a || !b) return 0;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function bearing(a, b) {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export function compassLabel(deg) {
  const labels = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return labels[Math.round(deg / 45) % 8];
}

export function compassFull(deg) {
  const map = {
    N: 'NORTH', NE: 'NORTHEAST', E: 'EAST', SE: 'SOUTHEAST',
    S: 'SOUTH', SW: 'SOUTHWEST', W: 'WEST', NW: 'NORTHWEST',
  };
  return map[compassLabel(deg)];
}

export function totalDistance(points) {
  if (!points || points.length < 2) return 0;
  let d = 0;
  for (let i = 1; i < points.length; i++) d += distance(points[i - 1], points[i]);
  return d;
}

/* Closest point on segment AB to point P (all lat/lng). Returns { point, distance }. */
function closestOnSegment(p, a, b) {
  // Convert to local planar approximation using equirectangular projection.
  const lat0 = toRad((a.lat + b.lat) / 2);
  const toXY = (pt) => ({ x: toRad(pt.lng) * Math.cos(lat0) * R, y: toRad(pt.lat) * R });
  const P = toXY(p);
  const A = toXY(a);
  const B = toXY(b);
  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((P.x - A.x) * dx + (P.y - A.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = A.x + t * dx;
  const cy = A.y + t * dy;
  const dd = Math.hypot(P.x - cx, P.y - cy);
  // Interpolated lat/lng
  const lat = a.lat + (b.lat - a.lat) * t;
  const lng = a.lng + (b.lng - a.lng) * t;
  return { point: { lat, lng }, distance: dd, segmentIndex: null, t };
}

/* Nearest point on the entire polyline. Returns { point, distance, segmentIndex, t }. */
export function nearestOnPolyline(p, points) {
  if (!points || points.length === 0) return null;
  if (points.length === 1) return { point: points[0], distance: distance(p, points[0]), segmentIndex: 0, t: 0 };
  let best = null;
  for (let i = 0; i < points.length - 1; i++) {
    const c = closestOnSegment(p, points[i], points[i + 1]);
    if (!best || c.distance < best.distance) {
      best = { ...c, segmentIndex: i };
    }
  }
  return best;
}

export function boundsOf(points) {
  if (!points || points.length === 0) return null;
  let minLat = points[0].lat, maxLat = points[0].lat;
  let minLng = points[0].lng, maxLng = points[0].lng;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return { minLat, maxLat, minLng, maxLng };
}

export function fmtDistance(m) {
  if (m == null || isNaN(m)) return '—';
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
}

export function fmtDuration(ms) {
  if (!ms || ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}
